/**
 * ReceiptOptionsSelector — Opciones de recibo combinables (1, 2 o las 2 de caja).
 *
 * «Sin recibos» es excluyente. «Automático» y «Manual» pueden ir juntos o por separado.
 */
import { Hand, Receipt, Zap } from "lucide-react";
import toast from "react-hot-toast";
import { SwitchRow } from "@/components/ui/Switch";
import type { PosPrinterConfig } from "@/lib/native/posPrinterConfig";

interface Props {
  cfg: PosPrinterConfig;
  onChange: (patch: Partial<PosPrinterConfig>) => void;
  className?: string;
}

export function ReceiptOptionsSelector({
  cfg,
  onChange,
  className = "",
}: Props) {
  const receiptsOff = !cfg.printReceipts;

  const enableReceipts = () => {
    onChange({
      printReceipts: true,
      autoPrintOnSale: true,
      manualReceiptOnSale: true,
    });
  };

  const disableReceipts = () => {
    onChange({
      printReceipts: false,
      autoPrintOnSale: false,
      manualReceiptOnSale: false,
    });
  };

  const setAuto = (next: boolean) => {
    if (receiptsOff) return;
    if (!next && !cfg.manualReceiptOnSale) {
      toast.error("Activa al menos impresión automática o botón manual.");
      return;
    }
    onChange({ autoPrintOnSale: next });
  };

  const setManual = (next: boolean) => {
    if (receiptsOff) return;
    if (!next && !cfg.autoPrintOnSale) {
      toast.error("Activa al menos impresión automática o botón manual.");
      return;
    }
    onChange({ manualReceiptOnSale: next });
  };

  return (
    <div className={className}>
      <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 dark:text-slate-500 mb-2 px-0.5">
        Recibos en caja
      </p>
      <p className="mb-2 px-0.5 text-[10px] text-gray-500 dark:text-slate-400 leading-snug">
        Marca las que necesites. Automático y manual pueden ir juntos; «Sin
        recibos» apaga las otras dos.
      </p>
      <div className="space-y-2">
        <SwitchRow
          icon={<Receipt size={16} />}
          label="Sin recibos"
          description="La venta termina sin ticket ni impresión."
          checked={receiptsOff}
          onChange={(v) => (v ? disableReceipts() : enableReceipts())}
        />
        <SwitchRow
          icon={<Zap size={16} />}
          label="Imprimir en cada venta"
          description="El ticket sale solo al cobrar."
          checked={!receiptsOff && cfg.autoPrintOnSale}
          onChange={setAuto}
          disabled={receiptsOff}
          lockReason={
            receiptsOff
              ? "Desactiva «Sin recibos» para usar esta opción."
              : undefined
          }
        />
        <SwitchRow
          icon={<Hand size={16} />}
          label="Solo cuando lo pida el cajero"
          description="Tras cobrar: botón «Imprimir ticket» y vista previa."
          checked={!receiptsOff && cfg.manualReceiptOnSale}
          onChange={setManual}
          disabled={receiptsOff}
          lockReason={
            receiptsOff
              ? "Desactiva «Sin recibos» para usar esta opción."
              : undefined
          }
        />
      </div>
    </div>
  );
}
